import { useMutation, useQueryClient } from "@tanstack/react-query";
import { neon } from "@/integrations/neon";
import type { CreateVersionInput } from "../types";

export const useCreateVersion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateVersionInput) => {
      const newVersion = await neon.api.versions.create({
        scriptId: input.scriptId,
        scriptUrl: input.scriptUrl,
        scriptUrlTh: input.scriptUrlTh,
        versionName: input.versionName,
        notes: input.notes,
        notesTh: input.notesTh,
        passwordProtected: input.passwordProtected,
        reviewPassword: input.reviewPassword,
      });

      return newVersion;
    },
    onMutate: async (variables) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["script-versions", variables.scriptId] });
      await queryClient.cancelQueries({ queryKey: ["scripts"] });

      // Snapshot previous values
      const previousVersions = queryClient.getQueryData(["script-versions", variables.scriptId]);
      const previousScripts = queryClient.getQueryData(["scripts"]);

      return { previousVersions, previousScripts };
    },
    onError: (_err, variables, context) => {
      // Rollback to previous state on error
      if (context?.previousVersions) {
        queryClient.setQueryData(["script-versions", variables.scriptId], context.previousVersions);
      }
      if (context?.previousScripts) {
        queryClient.setQueryData(["scripts"], context.previousScripts);
      }
    },
    onSuccess: (_, variables) => {
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ["script-versions", variables.scriptId] });
      queryClient.invalidateQueries({ queryKey: ["scripts"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
};
